/**
 * Permission Request Batcher
 * Collects multiple permission check requests and batches them into a single API call
 * to reduce network overhead and improve performance
 */

class PermissionBatcher {
  constructor(batchDelay = 50, maxBatchSize = 50) {
    this.batchDelay = batchDelay; // milliseconds to wait before sending batch
    this.maxBatchSize = maxBatchSize; // maximum permissions per batch
    this.queue = [];
    this.timer = null;
    this.processBatch = null; // callback function to process the batch
  }

  /**
   * Set the batch processing callback
   * @param {Function} callback - Function to call with batched permissions
   */
  setProcessor(callback) {
    this.processBatch = callback;
  }

  /**
   * Add a permission check to the batch queue
   * @param {Object} permission - Permission object {resourceType, action, resourceId}
   * @returns {Promise} Promise that resolves with the permission result
   */
  add(permission) {
    return new Promise((resolve, reject) => {
      // Add to queue with promise handlers
      this.queue.push({
        permission,
        resolve,
        reject,
      });

      // If we've reached max batch size, process immediately
      if (this.queue.length >= this.maxBatchSize) {
        this.flush();
      } else {
        // Schedule batch processing
        this.scheduleBatch();
      }
    });
  }

  /**
   * Schedule batch processing after delay
   */
  scheduleBatch() {
    // Clear existing timer
    if (this.timer) {
      clearTimeout(this.timer);
    }

    // Schedule new batch
    this.timer = setTimeout(() => {
      this.flush();
    }, this.batchDelay);
  }

  /**
   * Immediately process all queued permissions
   */
  async flush() {
    // Clear scheduled timer
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    // Nothing to process
    if (this.queue.length === 0) {
      return;
    }

    // Extract current queue and reset
    const batch = this.queue.splice(0);

    // Extract permissions for API call
    const permissions = batch.map((item) => item.permission);

    try {
      if (!this.processBatch) {
        throw new Error("Batch processor not configured");
      }

      // Process the batch
      const results = await this.processBatch(permissions);

      // Resolve each promise with its corresponding result
      batch.forEach((item, index) => {
        const result = results[index];
        if (result && !result.error) {
          item.resolve(result);
        } else {
          item.reject(
            new Error(result?.error || "Permission evaluation failed")
          );
        }
      });
    } catch (error) {
      // Reject all promises in this batch
      batch.forEach((item) => {
        item.reject(error);
      });
    }
  }

  /**
   * Clear all pending batches
   */
  clear() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    // Reject all pending promises
    this.queue.forEach((item) => {
      item.reject(new Error("Batch cleared"));
    });

    this.queue = [];
  }

  /**
   * Get current queue size
   * @returns {number}
   */
  size() {
    return this.queue.length;
  }
}

// Create and export a singleton instance
const permissionBatcher = new PermissionBatcher(50, 50);

export default permissionBatcher;
export { PermissionBatcher };
